import { useCallback, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Ban, Globe, Hash, History, ShieldCheck, TrendingUp } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { fetchCountries, createFourEyesRequest, type CountryRow } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { DangerDialog } from '@/components/DangerDialog'
import { useToast } from '@/providers/UiProviders'

/**
 * A11.02 — Davlatlar ro'yxati + A11.04 davlatni bloklash/ochish.
 *
 * A11.04 endi FOUR-EYES PRODUCER: bloklash/ochish bir kliklik EMAS — u
 * 4-darajali four-eyes SO'ROVINI yaratadi (DangerDialog L4 → POST /admin/
 * four-eyes). So'rov A18.06 navbatiga tushadi, boshqa admin tasdiqlaydi,
 * so'rovchi bajaradi (davlat o'shanda bloklanadi). Backend `ab354dc`.
 * TZ A11: «Davlatni yopish — four-eyes + sabab MAJBURIY».
 */
export function CountriesScreen() {
  const t = useT()
  const { toast } = useToast()
  const [target, setTarget] = useState<{ row: CountryRow; block: boolean } | null>(null)

  const columns = useMemo<AdminColumn<CountryRow>[]>(
    () => [
      {
        key: 'flag',
        header: '',
        type: 'text',
        width: 44,
        sortable: false,
        filterable: false,
        render: (r) => <span className="text-base">{r.flag || '🌐'}</span>,
      },
      { key: 'code', header: t.col.code, type: 'id', width: 80 },
      {
        key: 'name',
        header: t.col.country,
        type: 'text',
        minWidth: 180,
        render: (r) => (
          <Link to={`/geo/${r.code}`} className="text-accent hover:underline">
            {r.name}
          </Link>
        ),
      },
      { key: 'nationality', header: t.col.nationality, type: 'text', width: 150, hiddenByDefault: true },
      { key: 'dial_code', header: t.col.dialCode, type: 'text', width: 90 },
      { key: 'users', header: t.col.registrations, type: 'number', width: 110 },
      {
        key: 'is_blocked',
        header: t.col.status,
        type: 'enum',
        width: 140,
        accessor: (r) => (r.is_blocked ? 'blocked' : 'open'),
        options: [
          { value: 'open', label: t.status.open, status: 'active' },
          { value: 'blocked', label: t.status.blocked, status: 'banned' },
        ],
      },
    ],
    [t],
  )

  const fetcher = useCallback(fetchCountries, [])

  const rowActions = useMemo<RowAction<CountryRow>[]>(
    () => [
      {
        id: 'block',
        label: t.geo.block,
        icon: <Ban size={13} />,
        danger: true,
        permission: PERMISSIONS.COUNTRIES_BLOCK,
        visible: (r) => !r.is_blocked,
        onSelect: (r) => setTarget({ row: r, block: true }),
      },
      {
        id: 'unblock',
        label: t.geo.unblock,
        icon: <ShieldCheck size={13} />,
        permission: PERMISSIONS.COUNTRIES_BLOCK,
        visible: (r) => r.is_blocked,
        onSelect: (r) => setTarget({ row: r, block: false }),
      },
    ],
    [t],
  )

  return (
    <>
      <PageHeader
        title={t.nav.geo}
        screenId="A11.02"
        verified
        actions={
          <div className="flex items-center gap-1.5">
            <Link
              to="/geo/growth"
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <TrendingUp size={13} />
              {t.geo.growth}
            </Link>
            <Link
              to="/geo/prefixes"
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <Hash size={13} />
              {t.geo.dialPrefixes}
            </Link>
            <Link
              to="/geo/changelog"
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <History size={13} />
              {t.geo.changelog}
            </Link>
          </div>
        }
      />
      <DataTable<CountryRow>
        screenId="A11.02"
        permission={PERMISSIONS.COUNTRIES_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => r.code}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <Globe size={28} strokeWidth={1.5} /> }}
      />

      {target && (
        <DangerDialog
          open
          onClose={() => setTarget(null)}
          screenId="A11.04"
          level={4}
          title={
            target.block
              ? t.geo.blockTitle(target.row.name)
              : t.geo.unblockTitle(target.row.name)
          }
          description={target.block ? t.geo.blockDesc : t.geo.unblockDesc}
          confirmLabel={target.block ? t.geo.block : t.geo.unblock}
          objectName={target.row.code}
          impact={{
            items: [{ label: t.geo.impactUsers, value: target.row.users, emphasis: target.block }],
            reversible: t.geo.reverseWindow,
          }}
          onCreateRequest={async ({ reason }) => {
            try {
              await createFourEyesRequest({
                action: target.block ? 'block_country' : 'unblock_country',
                target: `country:${target.row.code}`,
                reason,
                impact: { users: target.row.users, country: target.row.code },
              })
              toast(t.geo.requestCreated, { tone: 'success' })
              // Dialog DangerDialog ichida yopiladi (onClose). Navigatsiya YO'Q —
              // submit tugamasdan unmount qilinmasin.
            } catch (e) {
              toast(t.geo.requestFailed, {
                tone: 'error',
                ...(e instanceof Error && e.message ? { description: e.message } : {}),
              })
              throw e // dialog ochiq qolsin (DangerDialog xatoda onClose chaqirmaydi)
            }
          }}
        />
      )}
    </>
  )
}
